/**
 * documentRepositoryApi.js — historia 170 (Repositorio Documental). The ONLY file that spells any
 * `documentRepository/**` route (impacto.md §Jupiter): every call goes through `generic/Api_Functions`
 * (`apiGet`/`apiPost`/`apiPut`/`apiDelete`), never a raw `fetch`. One function per endpoint of the
 * frozen contract (`03-dominio.md` §"Contrato cross-repo congelado", R1-R8).
 *
 * Wired: R1 (`getDocumentRepositoryTopics`, J0), R2 (`searchDocumentRepository`, J0), R3
 * (`searchDocumentRepositoryRecords`, J2), R6 (`uploadDocumentRepositoryDocument`, J3), R7
 * (`updateDocumentRepositoryCard`, J3), R8 (`deleteDocumentRepositoryDocument`, J3). R4/R5 only need
 * a URL (bytes response, no JSON) — `SmartDocumentViewer`'s additive `previewUrl` prop (J3) and
 * `apiDownloadFile` consume them directly, so only the builder lives here. R6 uses `apiPutFormData`
 * (generic multipart PUT, already in `Api_Functions`) — NOT `apiFile`, whose form fields are hardcoded
 * to the legacy `document` module (`order`,`forMosNextJobs`,`jobs`...) and would not fit this shape.
 *
 * D10 (`contrato-sun.md` §7, `encargo-sun-r2-request-pagination.md`) — R2 now receives the STANDARD
 * `RequestPagination` shape every other paginated table in Jupiter already sends
 * (`generic/hooks/usePagination/usePagination.jsx`), not a bespoke DTO. `toDocumentRepositorySearchRequest`
 * is the ONLY translation this file does now: it takes `SmartMateriaUITable`'s own `requestPagination`
 * (almost) as-is and patches exactly the 3 things Sun's contract carves out specially:
 *   - `columnFilters`/`columnFilterValues`: keeps ONLY the 6 fields Sun resolves by substring
 *     (`documentName`, `fileName`, `description`, `documentType.name`, `extension`, `createdUser`); strips `topic` and
 *     `createdDate` (Sun 400s on either, they travel elsewhere) and any blank value.
 *   - `mixedColumnFilters`: Sun accepts ONLY the key `topic` (`{"topic":[literal,…]}` — any other key
 *     400s) — `usePagination.jsx`'s own default (one empty-array key PER COLUMN) is REPLACED wholesale,
 *     never merged.
 *   - `parameterFilters`/`parameterFiltersValues`: `recordTopic`/`recordReferenceId` (S48, screen-owned,
 *     never a column filter) and `createdFromMillis`/`createdToMillis` (RN28 full-day millis, still
 *     computed client-side — Sun does not know the browser's timezone).
 * Everything else (`genericSearch`, `page`, `size`, `sortKey`, `sortMode`, and every field Sun ignores —
 * `columns`, `rows`, `primaryKeys`, `allSelected`, `columnFilterOperators`…) travels UNCHANGED: Sun
 * translates `sortKey[0]`'s own column `field` into its sort enum itself now (no more
 * `SORT_KEY_BY_FIELD` map on this side), and substring resolution for the 6 kept `columnFilters` fields
 * happens entirely in Sun (RN31 is Sun's job now, not this file's).
 */
import { apiDelete, apiGet, apiPost, apiPut, apiPutFormData } from '../../generic/Api_Functions'
import { createdDateRangeFromColumnFilter } from './documentRepositoryDateRange'

const BASE = 'documentRepository'

const encodeSegment = (value) => encodeURIComponent(String(value))

const documentPath = (topic, referenceId, documentId) =>
  BASE + '/documents/' + encodeSegment(topic) + '/' + encodeSegment(referenceId) + '/' + documentId

// Mirrors `documentRepositoryUrlState.js`'s own `DEFAULT_CRITERIA.sortKey`/`sortDirection` — the sort
// `requestPagination` falls back to on the rare occasion neither the user's own column click nor the
// screen's `sortOptions` default resolved one (defensive, matches `usePagination.jsx`'s own always-set
// `sortOptions` usage in `DocumentRepository.jsx`).
const DEFAULT_SORT_FIELD = 'createdDate'
const DEFAULT_SORT_DIRECTION = -1

// ---- R1 — GET documentRepository/topics -------------------------------------------------------
export const getDocumentRepositoryTopics = () => apiGet(BASE + '/topics')

// ---- Reused existing endpoint (NOT R1-R8) — the type filter (J2, S49) and the upload dialog (J3)
// both need the document type catalogue Jupiter already fetches elsewhere (impacto.md §Jupiter:
// "reutiliza GET documentTypes"). Kept in this file anyway so the whole screen has one source of
// truth for every network call it makes, reused or not.
export const getDocumentTypes = () => apiGet('documentTypes')

// ---- R2 — POST documentRepository/documents/search ---------------------------------------------

/** Posts an already-built `RequestPagination` body (D10) — the body itself is always built by
 * `toDocumentRepositorySearchRequest` (the table) or `documentRepositoryRecordSearchRequest` (the
 * upload dialog's duplicate check), never assembled ad hoc at the call site. */
export const searchDocumentRepository = (body) => apiPost(BASE + '/documents/search', body)

/**
 * `DocumentRepositoryUploadDialog.jsx`'s own use of R2 (S75): "does a document with this same name and
 * file already exist on this record" — a plain record-scoped search, no column filters, no generic
 * text, no sort the caller cares about (RN28/V5 do not apply either, there is no date range here).
 * A minimal, valid `RequestPagination` body (D10): Sun ignores whatever it does not need.
 */
export const documentRepositoryRecordSearchRequest = (recordTopic, recordReferenceId, size = 25) => ({
  genericSearch: '',
  columnFilters: [],
  columnFilterValues: [],
  mixedColumnFilters: '{}',
  parameterFilters: ['recordTopic', 'recordReferenceId'],
  parameterFiltersValues: [recordTopic, recordReferenceId],
  page: 0,
  size,
  sortKey: [DEFAULT_SORT_FIELD],
  sortMode: [DEFAULT_SORT_DIRECTION]
})

/**
 * documentRepository refactor — the screen's table is `generic/SmartMateriaUITable.jsx` in `pageable`
 * mode: it owns page/size/sort/general-search/column-filters through its own `requestPagination`
 * (`usePagination.jsx`). `columnFilterEntry` reads one column's `(value, operator)` pair out of that
 * request's parallel arrays — used both to keep the 6 substring fields (Sun resolves them, this file
 * just forwards their raw text) and to pull out `topic`/`createdDate`, whose values need translating
 * before they can travel (literal codes / full-day millis) rather than forwarding as-is.
 */
const columnFilterEntry = (requestPagination, field) => {
  const fields = requestPagination.columnFilters || []
  const index = fields.indexOf(field)
  if (index === -1) return { value: '', operator: '' }
  return {
    value: (requestPagination.columnFilterValues || [])[index] || '',
    operator: (requestPagination.columnFilterOperators || [])[index] || ''
  }
}

const splitFilterFragments = (rawValue) => String(rawValue || '').split(',').map((fragment) => fragment.trim().toLowerCase()).filter(Boolean)

const matchesAnyFragment = (label, fragments) => fragments.some((fragment) => String(label).toLowerCase().includes(fragment))

/** Topic column (translated label, comma-separated fragments) -> the matching RAW topic literals,
 * restricted to what R1 actually offered (RN31) — never any of the other ~20 topics this build knows
 * the i18n label of. The ONLY resolution against a Jupiter-side catalogue D10 still leaves on this
 * side: Sun's `mixedColumnFilters.topic` wants `DocumentTopic` literals, never a translated label. */
export const topicsFromColumnFilterValue = (rawValue, topics, translateTopicLabel) => {
  const fragments = splitFilterFragments(rawValue)
  if (fragments.length === 0) return []
  return topics.map((topic) => topic.topic).filter((code) => matchesAnyFragment(translateTopicLabel(code), fragments))
}

// D10 — the only 6 `columnFilters` fields Sun accepts (substring, resolved server-side; `description`
// since #3026); every other field (`topic`, `createdDate`, `record`, or anything unmapped) 400s and
// must never be forwarded.
const ALLOWED_COLUMN_FILTER_FIELDS = ['documentName', 'fileName', 'description', 'documentType.name', 'extension', 'createdUser']

/**
 * `SmartMateriaUITable`'s own `requestPagination` -> R2's `RequestPagination` body (D10). `recordFilter
 * = { recordTopic, recordReferenceId }` is the ONE piece of criteria the table itself does not own
 * (S48, screen-owned — see `DocumentRepository.jsx`'s own header comment on `criteria`).
 * `catalogs = { topics, translateTopicLabel }` is only ever needed to resolve the Topic column's typed
 * label into the literals `mixedColumnFilters` wants (RN31) — no other field needs a catalogue anymore,
 * D10 moved every other resolution (type/extension/uploaded-by) into Sun itself.
 */
export const toDocumentRepositorySearchRequest = (requestPagination, recordFilter = {}, catalogs = {}) => {
  const recordTopic = recordFilter.recordTopic || ''
  const recordReferenceId = recordFilter.recordReferenceId || ''
  const topics = catalogs.topics || []
  const translateTopicLabel = catalogs.translateTopicLabel || ((code) => code)

  const fields = requestPagination.columnFilters || []
  const values = requestPagination.columnFilterValues || []
  const keptIndexes = fields
    .map((_field, index) => index)
    .filter((index) => ALLOWED_COLUMN_FILTER_FIELDS.includes(fields[index]) && values[index])

  const topicEntry = columnFilterEntry(requestPagination, 'topic')
  const topicCodes = topicsFromColumnFilterValue(topicEntry.value, topics, translateTopicLabel)

  const dateEntry = columnFilterEntry(requestPagination, 'createdDate')
  const dateRange = createdDateRangeFromColumnFilter(dateEntry.value, dateEntry.operator)

  const parameterFilters = []
  const parameterFiltersValues = []
  const addParameter = (name, value) => {
    if (!value) return
    parameterFilters.push(name)
    parameterFiltersValues.push(String(value))
  }
  addParameter('recordTopic', recordTopic)
  addParameter('recordReferenceId', recordReferenceId)
  addParameter('createdFromMillis', dateRange.createdFromMillis)
  addParameter('createdToMillis', dateRange.createdToMillis)

  return {
    ...requestPagination,
    columnFilters: keptIndexes.map((index) => fields[index]),
    columnFilterValues: keptIndexes.map((index) => values[index]),
    mixedColumnFilters: JSON.stringify(topicCodes.length > 0 ? { topic: topicCodes } : {}),
    parameterFilters,
    parameterFiltersValues
  }
}

// The URL param each column-filter field patches back into `criteria` (`documentRepositoryUrlState.js`
// own field names) — mirrors `ALLOWED_COLUMN_FILTER_FIELDS` plus `topic` (screen-owned display text,
// see that file's own header comment on why `topicFilter` stores the raw typed label, not a literal).
const COLUMN_FILTER_URL_FIELD = {
  documentName: 'documentNameFilter',
  fileName: 'fileNameFilter',
  description: 'descriptionFilter',
  'documentType.name': 'documentTypeFilter',
  extension: 'extensionFilter',
  createdUser: 'createdUserFilter',
  topic: 'topicFilter'
}

/**
 * The RESOLVED criteria fields the table's own `requestPagination` decides (`page`, `size`, `sortKey`,
 * `sortDirection`, `text`, and every column filter, RAW TEXT — D10 no longer resolves any of them
 * against a catalogue on this side) — in the CRITERIA's own sentinel style (`''`, never `null`,
 * CLAUDE.md), not R2's. `DocumentRepository.jsx` merges this into its own `criteria` state on every
 * `dataFetch` call, so the query string (CA4.3, S94/S95) always reflects whatever sort/page/size/text/
 * filters the user last landed on through the table itself.
 */
export const criteriaPatchFromTableRequest = (requestPagination) => {
  const patch = {
    text: requestPagination.genericSearch || '',
    page: requestPagination.page,
    size: requestPagination.size,
    sortKey: (requestPagination.sortKey && requestPagination.sortKey[0]) || DEFAULT_SORT_FIELD,
    sortDirection: (requestPagination.sortMode && requestPagination.sortMode[0]) || DEFAULT_SORT_DIRECTION
  }
  Object.entries(COLUMN_FILTER_URL_FIELD).forEach(([tableField, urlField]) => {
    patch[urlField] = columnFilterEntry(requestPagination, tableField).value
  })
  const dateEntry = columnFilterEntry(requestPagination, 'createdDate')
  const dateRange = createdDateRangeFromColumnFilter(dateEntry.value, dateEntry.operator)
  patch.createdFromMillis = dateRange.createdFromMillis
  patch.createdToMillis = dateRange.createdToMillis
  return patch
}

/** R2's response (`{content, totalElements, page, size}`) -> `SmartMateriaUITable`'s own pageable
 * shape for a `dataFetch` function (`SmartMateriaUITable.jsx:670-671`): it reads `pageableObject.number`
 * (Spring's own field name), never `.page` — R2's DTO uses `page` (03-dominio.md), so this is the
 * only place that rename happens. Unchanged by D10 (`contrato-sun.md` §3 confirms the response shape
 * itself did not change). */
export const documentRepositoryTablePage = (response) => ({
  content: (response && response.content) || [],
  totalElements: (response && response.totalElements) || 0,
  number: (response && response.page) || 0,
  size: (response && response.size) || 0
})

// ---- R3 — GET documentRepository/topics/{topic}/records?text=&page=&size= ----------------------
// Wired starting J2 (upload's record picker, filter by record). `size` default 25 / max 50 (G2-3):
// validated by Sun, not duplicated here.
export const searchDocumentRepositoryRecords = (topic, text, page = 0, size = 25) =>
  apiGet(BASE + '/topics/' + encodeSegment(topic) + '/records?' + new URLSearchParams({ text: text || '', page, size }).toString())

// ---- R4 — GET .../preview[?targetExtension=] ----------------------------------------------------
// Wired starting J3 (SmartDocumentViewer's additive URL prop): only the URL is built here, the
// component itself streams the bytes via its existing `<img>`/viewer wiring.
export const documentRepositoryPreviewUrl = (topic, referenceId, documentId, targetExtension) => {
  const query = targetExtension ? '?targetExtension=' + encodeSegment(targetExtension) : ''
  return documentPath(topic, referenceId, documentId) + '/preview' + query
}

// ---- R5 — GET .../download ------------------------------------------------------------------
// Wired starting J3 (a plain download link/button; `apiDownloadFile` already knows how to stream a
// URL to the browser given a filename).
export const documentRepositoryDownloadUrl = (topic, referenceId, documentId) =>
  documentPath(topic, referenceId, documentId) + '/download'

// ---- R6 — PUT documentRepository/documents/{topic}/{referenceId} (multipart) -------------------
// Wired starting J3 (upload dialog, CU5/S74). `apiPutFormData` (generic multipart PUT, already in
// Api_Functions) is the right fit — NOT `apiFile`, which is hardcoded to the legacy `document`
// module's own fields (`order`,`forMosNextJobs`,`jobs`...) and always PUTs a fixed path shape.
export const documentRepositoryUploadPath = (topic, referenceId) =>
  BASE + '/documents/' + encodeSegment(topic) + '/' + encodeSegment(referenceId)

/**
 * `{ file, documentName, documentType, description }` -> multipart PUT (R6). `description` and
 * `documentType` travel as `''` when absent (never `null`/`undefined`, CLAUDE.md) — same convention
 * as every other criteria field in this module.
 */
export const uploadDocumentRepositoryDocument = (topic, referenceId, { file, documentName, documentType, description }) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('documentName', documentName)
  formData.append('documentType', documentType === '' || documentType === null || documentType === undefined ? '' : String(documentType))
  formData.append('description', description || '')
  return apiPutFormData(documentRepositoryUploadPath(topic, referenceId), formData)
}

// ---- R7 — PUT .../documents/{topic}/{referenceId}/{documentId} (JSON) ---------------------------
// Wired starting J3 (edit card dialog, CU6). Same convention as R2: the dialog's "no type selected"
// sentinel (`''`, never `null`/`undefined` in its own state) is translated to `null` ONLY here, at
// the API boundary.
export const updateDocumentRepositoryCard = (topic, referenceId, documentId, cardUpdate) =>
  apiPut(documentPath(topic, referenceId, documentId), {
    ...cardUpdate,
    documentTypeId: cardUpdate.documentTypeId === '' ? null : cardUpdate.documentTypeId
  })

// ---- R8 — DELETE .../documents/{topic}/{referenceId}/{documentId} ------------------------------
// Wired starting J3 (delete confirmation, CU7).
export const deleteDocumentRepositoryDocument = (topic, referenceId, documentId) =>
  apiDelete(documentPath(topic, referenceId, documentId))
